import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  TextField,
  Typography,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import axios from 'axios';
import { SiteTakeoffProvider } from './SiteTakeoffContext';
import SiteTakeoffHeader from './header/SiteTakeoffHeader';
import Sidebar from './sidebar/Sidebar';
import MapContainer from './map/MapContainer';
import OperationalPropertyGroupBanner from './group/OperationalPropertyGroupBanner';
import PropertySettingsDrawer from './drawer/PropertySettingsDrawer';
import BulkEditDrawer from './drawer/BulkEditDrawer';
import SiteTakeoffTour from './SiteTakeoffTour';
import ServiceFlavorsWorkspace from './estimate/flavors/ServiceFlavorsWorkspace';
import useJobsite, { getJobsiteCoords } from './hooks/useJobsite';
import useAreas from './hooks/useAreas';
import useAreaRendering from './hooks/useAreaRendering';
import useAreaEditing from './hooks/useAreaEditing';
import useAreaDrawing from './hooks/useAreaDrawing';
import useOverlays from './hooks/useOverlays';
import useGeofence from './hooks/useGeofence';
import useSetupChecklist from './hooks/useSetupChecklist';
import usePropertyLines from './hooks/usePropertyLines';
import useRestrictedAreas from './hooks/useRestrictedAreas';
import useServiceAreas from './hooks/useServiceAreas';
import useOperationalPropertyGroup from './hooks/useOperationalPropertyGroup';
import useJobsiteDrawing from './hooks/useJobsiteDrawing';
import useDataLayerVisibility from './hooks/useDataLayerVisibility';
import usePlacementQueue from './hooks/usePlacementQueue';
import {
  SITE_TAKEOFF_V2_TOUR_STEPS,
  SITE_TAKEOFF_V2_TOUR_STORAGE_KEY,
} from './tourConfig';
import { AREA_TYPES } from '../SiteTakeoff/siteTakeoffConstants';

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 64px)',
    background: '#f5f5f5',
    position: 'relative',
    overflow: 'hidden',
  },
  body: {
    flex: 1,
    display: 'flex',
    position: 'relative',
    overflow: 'hidden',
  },
  mapArea: {
    flex: 1,
    position: 'relative',
    minWidth: 0,
    overflow: 'hidden',
  },
  quickStartFields: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
    maxWidth: 560,
  },
  quickStartFull: {
    gridColumn: '1 / -1',
  },
}));

const getClientName = (client) => (
  client?.displayName || client?.searchName || client?.companyName || client?.name || ''
);

const emptyQuickStartDraft = {
  siteName: '',
  address: '',
  address2: '',
  city: '',
  state: 'AZ',
  zip: '',
};

const SiteTakeoffV3 = (props) => {
  const classes = useStyles();
  const { jobsiteId } = useParams();
  const history = useHistory();
  const location = useLocation();

  // Map refs
  const mapRef = useRef(null);
  const drawnItemsRef = useRef(null);
  const tileLayerRef = useRef(null);
  const propertyLinesReloadRef = useRef(null);

  // UI state
  const [activeMode, setActiveMode] = useState(() => {
    const search = typeof window !== 'undefined' ? window.location.search : '';
    return new URLSearchParams(search).get('mode') === 'estimate' ? 'estimate' : 'areas';
  });
  const [settingsDrawerOpen, setSettingsDrawerOpen] = useState(false);
  const [editingAreaId, setEditingAreaId] = useState(null);
  const [draggableAreaId, setDraggableAreaId] = useState(null);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [tourStepIndex, setTourStepIndex] = useState(0);
  const [toastMsg, setToastMsg] = useState('');
  const [hiddenAreaIds, setHiddenAreaIds] = useState(new Set());
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [parcelInspectorEnabled, setParcelInspectorEnabled] = useState(false);
  const [quickStartOpen, setQuickStartOpen] = useState(false);
  const [quickStartSaving, setQuickStartSaving] = useState(false);
  const [quickStartDraft, setQuickStartDraft] = useState(emptyQuickStartDraft);
  const [quickStartClient, setQuickStartClient] = useState(null);
  const [quickStartClientInput, setQuickStartClientInput] = useState('');
  const pendingNavigationRef = useRef(null);
  const bypassLeaveGuardRef = useRef(false);

  const toggleAreaVisibility = useCallback((areaId) => {
    setHiddenAreaIds(prev => {
      const next = new Set(prev);
      if (next.has(areaId)) next.delete(areaId);
      else next.add(areaId);
      return next;
    });
  }, []);

  // Data layer visibility (extracted to hook)
  const { dataLayersVisible } = useDataLayerVisibility();

  // Header pickers state
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedJobsite, setSelectedJobsite] = useState(null);

  // Estimate rate seed (entity/customer labor rates for EstimateMode)
  const [estimateRateSeed, setEstimateRateSeed] = useState({ rates: {}, provenance: {} });

  // Load full jobsite data
  const { jobsite: loadedJobsite, legacyAreas, loading: jobsiteLoading, reload: reloadJobsite } = useJobsite(props.url, jobsiteId);

  // Manual site-pin flow for jobsites whose address couldn't be geocoded
  const [settingSitePin, setSettingSitePin] = useState(false);
  const needsSitePin = !!loadedJobsite?._id
    && (!!loadedJobsite.noGeoCoords || !getJobsiteCoords(loadedJobsite));

  useEffect(() => { setSettingSitePin(false); }, [loadedJobsite?._id]);

  const handleSetSitePin = useCallback(async (latlng) => {
    if (!loadedJobsite?._id || !latlng) return;
    try {
      const resp = await axios.put(
        `${props.url}/api/customer/jobsite/${loadedJobsite._id}/location`,
        { latitude: latlng.lat, longitude: latlng.lng }
      );
      if (resp.data?.success) {
        setSettingSitePin(false);
        setToastMsg('Site location saved');
        reloadJobsite();
      } else {
        setToastMsg(resp.data?.message || 'Failed to save site location');
      }
    } catch (err) {
      setToastMsg(err.response?.data?.message || 'Failed to save site location');
    }
  }, [loadedJobsite?._id, props.url, reloadJobsite]);

  // Load takeoff / areas
  const {
    areas, takeoffs, takeoff, loading: areasLoading, saving, dirty,
    addArea, updateArea, deleteArea, duplicateArea, saveTakeoff,
    groupAreas, ungroupArea, ungroupAll, replaceAreaWithAreas,
    pushUndoSnapshot, undoLastChange, canUndo, undoLabel,
    newTakeoff, reloadLatestTakeoff, switchTakeoff, deleteTakeoff, renameTakeoff,
    updateProductionRates, importLegacyAreas,
    ignoredLegacyAreaIds, ignoreLegacyAreas, restoreIgnoredLegacyAreas,
    updatePlacementQueue,
  } = useAreas(props.url, loadedJobsite);

  // A property group is a first-class takeoff view. Its canonical areas are
  // available from every member property; split mode reveals the property slices.
  const operationalGroup = useOperationalPropertyGroup({
    url: props.url,
    jobsite: loadedJobsite,
    mapRef,
    visible: dataLayersVisible.takeoffAreas !== false,
    currentPropertyLinesVisible: dataLayersVisible.propertyLines === true,
    redrawKey: activeMode,
    onProjectedAreasChanged: () => {
      if (dirty) {
        setToastMsg('Group areas were updated. Save or reload this takeoff to see the synchronized areas.');
        return;
      }
      reloadLatestTakeoff();
    },
    onMemberBoundariesChanged: () => propertyLinesReloadRef.current?.(),
  });

  const mapAreas = useMemo(() => {
    if (!operationalGroup.group || operationalGroup.viewMode !== 'combined') return areas;
    return areas.filter(area => !area.operationalPropertyGroupActive);
  }, [areas, operationalGroup.group, operationalGroup.viewMode]);

  const legacyImportCandidates = useMemo(() => {
    const ignored = new Set(ignoredLegacyAreaIds || []);
    return (legacyAreas || [])
      .map((area, index) => ({
        ...area,
        importId: area?._id || area?.id || area?.clientId || `legacy_${index}`,
      }))
      .filter(area => !ignored.has(area.importId));
  }, [ignoredLegacyAreaIds, legacyAreas]);

  // Show legacy import when we have old jobsite.areas but no takeoffs yet
  const hasLegacyAreas =
    (legacyAreas?.length || 0) > 0 && takeoffs.length === 0 && !areasLoading;

  // Render areas on the map
  const handleAreaMoved = useCallback((areaId, newPaths) => {
    updateArea(areaId, { paths: newPaths });
  }, [updateArea]);

  // layerMapRef is shared between rendering and editing hooks
  const layerMapRef = useRef({});

  // Shape editing must be declared before rendering so editingShapeAreaId is available
  const {
    editingAreaId: editingShapeAreaId,
    startEdit: startEditShape,
    stopEdit: stopEditShape,
  } = useAreaEditing({
    mapRef, layerMapRef, areas, updateArea,
  });

  const handleRenderedAreaClick = useCallback((areaId) => {
    if (editingShapeAreaId && editingShapeAreaId !== areaId) {
      stopEditShape();
    }
    setEditingAreaId(areaId);
    setDraggableAreaId(current => (current === areaId ? current : null));
  }, [editingShapeAreaId, stopEditShape]);

  useAreaRendering({
    mapRef, drawnItemsRef, areas: mapAreas,
    onAreaClick: handleRenderedAreaClick,
    visible: dataLayersVisible.takeoffAreas !== false,
    activeAreaId: editingAreaId,
    draggableAreaId,
    onAreaMoved: handleAreaMoved,
    hiddenAreaIds,
    showMeasurements: dataLayersVisible.measurements === true,
    editingShapeAreaId,
    layerMapRef,
  });

  const zoomToArea = useCallback((areaId) => {
    const layer = layerMapRef.current[areaId];
    const map = mapRef.current;
    if (layer && map) {
      try { map.fitBounds(layer.getBounds().pad(0.3), { animate: true }); } catch (_) {}
    }
  }, [layerMapRef, mapRef]);

  // Area drawing tools
  const { startDraw, snapping, setSnapping } = useAreaDrawing({
    mapRef, drawnItemsRef,
    onAreaDrawn: (newAreaDraft) => {
      const created = addArea(newAreaDraft);
      setEditingAreaId(created.id);
    },
  });

  const placementQueue = takeoff?.placementQueue || [];
  const {
    placingItemId,
    setPlacingItemId,
    placementPaused,
    handlePlacementOverlayClick,
  } = usePlacementQueue({
    mapRef,
    placementQueue,
    addArea,
    updatePlacementQueue,
    setEditingAreaId,
  });

  // Draw an exclusion zone for a parent area
  const handleDrawExclusion = useCallback((parentAreaId) => {
    setEditingAreaId(null);
    startDraw('polygon', {
      parentAreaId,
      exclusionType: 'other',
      areaType: AREA_TYPES.DO_NOT_SERVICE,
      name: 'Exclusion',
    });
  }, [startDraw]);

  // Jobsite-layer drawing (property lines, restricted areas)
  const {
    startPolygonDraw,
    startPolygonEdit,
    confirmEdit,
    cancelEdit,
    undoEdit,
    undoDrawPoint,
    cancelDraw,
    editing: editingShape,
    drawing: drawingJobsitePolygon,
    drawVertexCount,
  } = useJobsiteDrawing(mapRef);

  // Geofence
  const geofence = useGeofence({
    url: props.url, jobsite: loadedJobsite, mapRef,
    visible: dataLayersVisible.geofence,
    redrawKey: activeMode,
  });

  // Human-confirmed setup checklist (geofence / property lines / takeoff sign-offs)
  const setupChecklist = useSetupChecklist(props.url, loadedJobsite);

  // Property lines
  const propertyLines = usePropertyLines({
    url: props.url, jobsite: loadedJobsite, mapRef,
    visible: dataLayersVisible.propertyLines,
    manualOverridesCounty: Boolean(operationalGroup.group),
    parcelInspectorEnabled,
    redrawKey: activeMode,
  });
  propertyLinesReloadRef.current = propertyLines.reload;

  // Restricted areas
  const restrictedAreas = useRestrictedAreas({
    url: props.url, jobsite: loadedJobsite, mapRef,
    visible: dataLayersVisible.restrictedAreas,
    redrawKey: activeMode,
  });

  // Service areas (visual-only "where we actually service")
  const serviceAreas = useServiceAreas({
    url: props.url, jobsite: loadedJobsite, mapRef,
    visible: dataLayersVisible.serviceAreas !== false,
    redrawKey: activeMode, // map is recreated on Estimate-tab roundtrips
  });

  // Plan overlays (takeoff-scoped)
  const overlayManager = useOverlays({
    url: props.url, mapRef,
    jobsite: loadedJobsite,
    initialOverlays: takeoff?.overlays,
  });
  const hasUnsavedChanges = dirty || !!overlayManager?.dirty;

  // Keep the header picker in sync with the loaded jobsite
  useEffect(() => {
    if (!loadedJobsite?._id) return;
    setSelectedJobsite({
      _id: loadedJobsite._id,
      name: loadedJobsite.name,
      address: loadedJobsite.address,
      city: loadedJobsite.city,
      state: loadedJobsite.state,
      client: loadedJobsite.client,
    });
    if (loadedJobsite.client) {
      const clientObj = typeof loadedJobsite.client === 'object'
        ? loadedJobsite.client
        : { _id: loadedJobsite.client };
      setSelectedClient(clientObj);
    }
  }, [loadedJobsite]);

  // Fetch category labor rate seed from entity/customer when jobsite changes
  useEffect(() => {
    if (!loadedJobsite?._id) return;
    let cancelled = false;
    axios.get(`${props.url}/api/proposal/takeoff-estimate/rate-seed`, { params: { jobsiteId: loadedJobsite._id } })
      .then(resp => { if (!cancelled && resp.data?.success) setEstimateRateSeed({ rates: resp.data.rates, provenance: resp.data.provenance }); })
      .catch(err => { console.error('rate-seed fetch failed:', err); });
    return () => { cancelled = true; };
  }, [loadedJobsite?._id, props.url]);

  // Wrap saveTakeoff to inject live overlay geometry at save time + show feedback
  const saveTakeoffWithOverlays = useCallback(async (name) => {
    const liveOverlays = overlayManager?.serializeForSave?.() || [];
    const result = await saveTakeoff(name, { overlays: liveOverlays });
    if (!result) {
      setToastMsg('Failed to save');
    } else if (result.areaErrors && result.areaErrors.length > 0) {
      const names = result.areaErrors.map(e => e.name).join(', ');
      setToastMsg(`Takeoff not saved — fix these areas and retry: ${names}`);
      return null;
    } else {
      overlayManager?.clearDirty?.();
      setToastMsg('Takeoff saved');
    }
    return result;
  }, [saveTakeoff, overlayManager]);

  // Create a maintenance proposal (QuickMtcProposal) from the current takeoff and
  // route to its builder. saveTakeoff (-> saveTakeoffWithOverlays) returns the saved
  // takeoff DOC (with _id) on success or null on failure, so the new id is reliable
  // even for a brand-new takeoff.
  const onCreateEstimateFromTakeoff = useCallback(async (payload) => {
    const resolvedJobsiteId = payload?.jobsiteId
      || loadedJobsite?._id
      || selectedJobsite?._id
      || jobsiteId
      || takeoff?.jobSite
      || null;
    // The server loads the takeoff by id, so it must be persisted first.
    let takeoffId = takeoff?._id;
    if (!takeoffId || hasUnsavedChanges) {
      const saved = await saveTakeoffWithOverlays(takeoff?.name);
      if (!saved) {
        setToastMsg('Save the takeoff before creating a proposal');
        return;
      }
      takeoffId = (saved && saved._id) || takeoff?._id;
    }
    if (!takeoffId) {
      setToastMsg('No takeoff to estimate');
      return;
    }
    try {
      const body = payload?.contract
        ? { takeoffId, contract: payload.contract }
        : { takeoffId, settings: payload };
      if (payload?.estimateId) body.estimateId = payload.estimateId;
      if (payload?.forceNew) body.forceNew = true;
      const resp = await axios.post(`${props.url}/api/proposal/takeoff-estimate/create-proposal`, {
        ...body,
        jobsiteId: resolvedJobsiteId,
      });
      if (resp.data?.success && resp.data.proposalId) {
        setToastMsg('Proposal created');
        const returnTo = resolvedJobsiteId
          ? `/site-takeoff-v3/${resolvedJobsiteId}?mode=estimate`
          : `${location.pathname}${location.search || '?mode=estimate'}`;
        history.push(`/mtc-proposals/${resp.data.proposalId}`, {
          returnTo,
          returnLabel: 'Site Takeoff Estimate',
        });
        return resp.data;
      } else {
        setToastMsg('Failed to create proposal');
      }
    } catch (err) {
      console.error('Create proposal from takeoff failed:', err);
      setToastMsg('Failed to create proposal');
    }
  }, [takeoff, hasUnsavedChanges, saveTakeoffWithOverlays, props.url, history, loadedJobsite?._id, selectedJobsite?._id, jobsiteId, location.pathname, location.search]);

  const runAfterDirtyGuard = useCallback((action) => {
    if (!hasUnsavedChanges || bypassLeaveGuardRef.current) {
      action();
      return;
    }
    pendingNavigationRef.current = action;
    setLeaveDialogOpen(true);
  }, [hasUnsavedChanges]);

  const confirmPendingNavigation = useCallback(() => {
    const action = pendingNavigationRef.current;
    pendingNavigationRef.current = null;
    setLeaveDialogOpen(false);
    bypassLeaveGuardRef.current = true;
    if (action) action();
    setTimeout(() => { bypassLeaveGuardRef.current = false; }, 0);
  }, []);

  const handleJobsiteChange = useCallback(async (jobsite) => {
    // Aspire-only result: promote to a local Jobsite first, then proceed with
    // the returned Jobsite as the selection. The server creates the Jobsite,
    // links matchedJobsiteId, and stamps aspirePropertyId on the new doc.
    if (jobsite?.source === 'aspire' && jobsite.aspirePropertyId) {
      try {
        const resp = await axios.post(
          `${props.url}/api/customer/aspire-property/${jobsite.aspirePropertyId}/promote`
        );
        if (resp.data?.success && resp.data.jobsite) {
          // eslint-disable-next-line no-param-reassign
          jobsite = resp.data.jobsite;
        } else {
          console.error('Aspire promote failed:', resp.data);
          return;
        }
      } catch (err) {
        console.error('Aspire promote error:', err);
        return;
      }
    }

    runAfterDirtyGuard(() => {
      setSelectedJobsite(jobsite);
      setEditingAreaId(null);
      if (jobsite?.client) {
        const clientObj = typeof jobsite.client === 'object' ? jobsite.client : { _id: jobsite.client };
        setSelectedClient(clientObj);
      }
      if (jobsite?._id) {
        history.push(`/site-takeoff-v3/${jobsite._id}`);
      }
    });
  }, [history, props.url, runAfterDirtyGuard]);

  const handleClientChange = useCallback((client) => {
    runAfterDirtyGuard(() => {
      setSelectedClient(client);
      setSelectedJobsite(null);
      setEditingAreaId(null);
      if (!client) {
        history.push('/site-takeoff-v3');
      }
    });
  }, [history, runAfterDirtyGuard]);

  const handleQuickStartClose = useCallback(() => {
    if (quickStartSaving) return;
    setQuickStartOpen(false);
  }, [quickStartSaving]);

  const handleQuickStartSubmit = useCallback(() => {
    const clientName = typeof quickStartClient === 'string'
      ? quickStartClient
      : quickStartClientInput;
    const clientId = quickStartClient && typeof quickStartClient === 'object'
      ? quickStartClient._id
      : null;

    const action = async () => {
      setQuickStartSaving(true);
      try {
        const resp = await axios.post(`${props.url}/api/customer/site-takeoff/quick-start-jobsite`, {
          ...quickStartDraft,
          clientId,
          clientName,
        });
        if (resp.data?.success && resp.data.jobsite) {
          const createdJobsite = resp.data.jobsite;
          setQuickStartOpen(false);
          setQuickStartDraft(emptyQuickStartDraft);
          setQuickStartClient(null);
          setQuickStartClientInput('');
          setSelectedJobsite(createdJobsite);
          setSelectedClient(resp.data.client || createdJobsite.client || quickStartClient);
          setEditingAreaId(null);
          if (createdJobsite.noGeoCoords) {
            setToastMsg("Couldn't find that address on the map — click the map to drop the site pin");
          }
          history.push(`/site-takeoff-v3/${createdJobsite._id}`);
        } else {
          setToastMsg(resp.data?.message || 'Unable to create site');
        }
      } catch (err) {
        setToastMsg(err.response?.data?.message || 'Unable to create site');
      } finally {
        setQuickStartSaving(false);
      }
    };

    runAfterDirtyGuard(action);
  }, [history, props.url, quickStartClient, quickStartClientInput, quickStartDraft, runAfterDirtyGuard]);

  // Setup status. "Areas mapped" is derived (a fact); the other three are explicit
  // human sign-offs from the setup-checklist hook (geofence confirm also flows
  // through its own hook, so honor either as confirmed).
  const setupStatus = {
    areasMapped: areas.length > 0,
    geofenceSet: !!setupChecklist.checklist.geofenceSet.confirmed || !!geofence?.confirmed,
    propertyLinesVerified: !!setupChecklist.checklist.propertyLinesVerified.confirmed,
    takeoffComplete: !!setupChecklist.checklist.takeoffComplete.confirmed,
  };
  const isSetupComplete = Object.values(setupStatus).every(Boolean);

  // Tour
  const currentTourStep = SITE_TAKEOFF_V2_TOUR_STEPS[tourStepIndex];

  const markTourSeen = useCallback(() => {
    try { localStorage.setItem(SITE_TAKEOFF_V2_TOUR_STORAGE_KEY, '1'); } catch (_) {}
  }, []);

  const closeTour = useCallback((markSeen = true) => {
    setTourOpen(false);
    setTourStepIndex(0);
    setSettingsDrawerOpen(false);
    if (markSeen) markTourSeen();
  }, [markTourSeen]);

  const launchTour = useCallback(() => {
    stopEditShape();
    setEditingAreaId(null);
    setActiveMode('areas');
    setSettingsDrawerOpen(false);
    setTourStepIndex(0);
    setTourOpen(true);
  }, [stopEditShape]);

  useEffect(() => {
    if (!loadedJobsite?._id) return;
    try { if (localStorage.getItem(SITE_TAKEOFF_V2_TOUR_STORAGE_KEY)) return; } catch (_) {}
    const timer = setTimeout(() => { launchTour(); }, 900);
    return () => clearTimeout(timer);
  }, [loadedJobsite?._id, launchTour]);

  useEffect(() => {
    if (!hasUnsavedChanges) return undefined;
    const onBeforeUnload = (event) => {
      event.preventDefault();
      // Required by browsers to trigger the native leave prompt.
      event.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (!history?.block) return undefined;
    if (!hasUnsavedChanges) return undefined;
    const unblock = history.block((nextLocation) => {
      if (bypassLeaveGuardRef.current) return undefined;
      pendingNavigationRef.current = () => history.push(nextLocation.pathname + nextLocation.search + nextLocation.hash);
      setLeaveDialogOpen(true);
      return false;
    });
    return unblock;
  }, [hasUnsavedChanges, history]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    if (params.get('mode') === 'estimate') {
      setActiveMode('estimate');
    }
  }, [location.search]);

  useEffect(() => {
    const onKeyDown = (event) => {
      const isUndo = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z';
      if (!isUndo) return;
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || document.activeElement?.isContentEditable) return;
      event.preventDefault();
      undoLastChange();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [undoLastChange]);

  useEffect(() => {
    if (!tourOpen || !currentTourStep) return;
    stopEditShape();
    setEditingAreaId(null);
    if (currentTourStep.id === 'geofence-section' || currentTourStep.id === 'property-lines-section') {
      setSettingsDrawerOpen(true);
    } else {
      setSettingsDrawerOpen(false);
    }
    if (currentTourStep.id === 'projects-upload') {
      setActiveMode('projects');
    } else {
      setActiveMode('areas');
    }
  }, [tourOpen, currentTourStep, stopEditShape]);

  const handleTourNext = useCallback(() => {
    if (tourStepIndex >= SITE_TAKEOFF_V2_TOUR_STEPS.length - 1) { closeTour(true); return; }
    setTourStepIndex(tourStepIndex + 1);
  }, [tourStepIndex, closeTour]);

  const handleTourBack = useCallback(() => {
    if (tourStepIndex <= 0) return;
    setTourStepIndex(tourStepIndex - 1);
  }, [tourStepIndex]);

  const handleTourTargetMissing = useCallback(() => {
    if (tourStepIndex >= SITE_TAKEOFF_V2_TOUR_STEPS.length - 1) { closeTour(true); return; }
    setTourStepIndex(tourStepIndex + 1);
  }, [tourStepIndex, closeTour]);

  // Assemble context value
  const contextValue = useMemo(() => ({
    url: props.url, clients: props.clients,
    jobsite: loadedJobsite, areas, takeoffs, takeoff, saving, dirty: hasUnsavedChanges,
    confirmDirtyAction: runAfterDirtyGuard,
    addArea, updateArea, deleteArea, duplicateArea, saveTakeoff: saveTakeoffWithOverlays,
    groupAreas, ungroupArea, ungroupAll, replaceAreaWithAreas,
    pushUndoSnapshot, undoLastChange, canUndo, undoLabel,
    newTakeoff, switchTakeoff, deleteTakeoff, renameTakeoff,
    updateProductionRates, importLegacyAreas,
    legacyAreas, hasLegacyAreas, legacyImportCandidates,
    ignoredLegacyAreaIds, ignoreLegacyAreas, restoreIgnoredLegacyAreas,
    placementQueue, updatePlacementQueue,
    placingItemId, setPlacingItemId, placementPaused,
    activeMode, setActiveMode,
    editingAreaId, setEditingAreaId,
    editingShapeAreaId, startEditShape, stopEditShape,
    draggableAreaId, setDraggableAreaId,
    bulkEditOpen, setBulkEditOpen,
    startDraw, snapping, setSnapping, handleDrawExclusion,
    zoomToArea, overlayManager,
    hiddenAreaIds, toggleAreaVisibility,
    settingsDrawerOpen, setSettingsDrawerOpen,
    geofence, propertyLines, operationalGroup, restrictedAreas, serviceAreas,
    startPolygonDraw, startPolygonEdit,
    editingShape, confirmEdit, cancelEdit, undoEdit,
    setupStatus, mapRef, drawnItemsRef, tileLayerRef,
    dataLayersVisible,
    estimateRateSeed,
    onCreateEstimateFromTakeoff,
  }), [
    props.url, props.clients, loadedJobsite,
    areas, takeoffs, takeoff, saving, hasUnsavedChanges,
    runAfterDirtyGuard,
    addArea, updateArea, deleteArea, duplicateArea, saveTakeoffWithOverlays,
    groupAreas, ungroupArea, ungroupAll, replaceAreaWithAreas,
    pushUndoSnapshot, undoLastChange, canUndo, undoLabel,
    newTakeoff, switchTakeoff, deleteTakeoff, renameTakeoff,
    updateProductionRates, importLegacyAreas,
    legacyAreas, hasLegacyAreas, legacyImportCandidates,
    ignoredLegacyAreaIds, ignoreLegacyAreas, restoreIgnoredLegacyAreas,
    placementQueue, updatePlacementQueue,
    placingItemId, setPlacingItemId, placementPaused,
    activeMode, editingAreaId,
    editingShapeAreaId, startEditShape, stopEditShape,
    draggableAreaId, bulkEditOpen,
    startDraw, snapping, setSnapping, handleDrawExclusion,
    zoomToArea, overlayManager,
    hiddenAreaIds, toggleAreaVisibility,
    settingsDrawerOpen,
    geofence, propertyLines, operationalGroup, restrictedAreas, serviceAreas,
    startPolygonDraw, startPolygonEdit,
    editingShape, confirmEdit, cancelEdit, undoEdit,
    setupStatus, dataLayersVisible,
    estimateRateSeed,
    onCreateEstimateFromTakeoff,
  ]);

  return (
    <SiteTakeoffProvider value={contextValue}>
      <div className={classes.root}>
        {activeMode === 'estimate' ? (
          <ServiceFlavorsWorkspace
            url={props.url}
            jobsite={loadedJobsite}
            areas={areas}
            takeoff={takeoff}
            rateSeed={estimateRateSeed || { rates: {}, provenance: {} }}
            onBackToTakeoff={() => setActiveMode('takeoff')}
            onGenerateProposal={onCreateEstimateFromTakeoff}
            onOpenProposal={(id) => {
              const resolvedJobsiteId = loadedJobsite?._id || selectedJobsite?._id || jobsiteId || takeoff?.jobSite || null;
              const returnTo = resolvedJobsiteId
                ? `/site-takeoff-v3/${resolvedJobsiteId}?mode=estimate`
                : `${location.pathname}${location.search || '?mode=estimate'}`;
              history.push(`/mtc-proposals/${id}`, { returnTo, returnLabel: 'Site Takeoff Estimate' });
            }}
            onToast={setToastMsg}
          />
        ) : (
          <>
            <SiteTakeoffHeader
              url={props.url}
              clients={props.clients}
              selectedClient={selectedClient}
              selectedJobsite={selectedJobsite}
              onClientChange={handleClientChange}
              onJobsiteChange={handleJobsiteChange}
              setupComplete={isSetupComplete}
              onSettingsClick={() => setSettingsDrawerOpen(true)}
              onGuideClick={launchTour}
              onQuickStartClick={() => setQuickStartOpen(true)}
            />

            <OperationalPropertyGroupBanner
              controller={operationalGroup}
              onManage={() => setSettingsDrawerOpen(true)}
            />

            <div className={classes.body}>
              <div className={classes.mapArea}>
                <MapContainer
              jobsite={loadedJobsite}
              loading={jobsiteLoading || areasLoading}
              mapRef={mapRef}
              drawnItemsRef={drawnItemsRef}
              tileLayerRef={tileLayerRef}
              editingShape={editingShape}
              onConfirmEdit={confirmEdit}
              onCancelEdit={cancelEdit}
              onUndoEdit={undoEdit}
              drawingJobsitePolygon={drawingJobsitePolygon}
              drawVertexCount={drawVertexCount}
              onUndoDrawPoint={undoDrawPoint}
              onCancelDraw={cancelDraw}
              placingItemId={placingItemId}
              placementPaused={placementPaused}
              onPlacementOverlayClick={handlePlacementOverlayClick}
              placementLabel={placementQueue.find(q => q.queueId === placingItemId)?.label}
              onCancelPlacement={() => setPlacingItemId(null)}
              parcelInspectorEnabled={parcelInspectorEnabled}
              onToggleParcelInspector={() => setParcelInspectorEnabled(v => !v)}
              needsSitePin={needsSitePin}
              settingSitePin={settingSitePin}
              onStartSetSitePin={() => setSettingSitePin(true)}
              onCancelSetSitePin={() => setSettingSitePin(false)}
              onSetSitePin={handleSetSitePin}
                />
              </div>

              <Sidebar />

              <BulkEditDrawer
                open={bulkEditOpen}
                onClose={() => setBulkEditOpen(false)}
                areas={areas}
                onUpdateArea={updateArea}
              />

              <PropertySettingsDrawer
                open={settingsDrawerOpen}
                onClose={() => setSettingsDrawerOpen(false)}
                url={props.url}
                jobsite={loadedJobsite}
                setupStatus={setupStatus}
                setupChecklist={setupChecklist}
                geofence={geofence}
                propertyLines={propertyLines}
                operationalGroup={operationalGroup}
                restrictedAreas={restrictedAreas}
                serviceAreas={serviceAreas}
                startPolygonDraw={startPolygonDraw}
                startPolygonEdit={startPolygonEdit}
                areas={areas}
                rateOverrides={takeoff?.productionRates || {}}
                onUpdateRates={updateProductionRates}
              />
            </div>
          </>
        )}

        <Snackbar
          open={!!toastMsg}
          autoHideDuration={3000}
          onClose={() => setToastMsg('')}
          message={toastMsg}
        />
        <Dialog open={quickStartOpen} onClose={handleQuickStartClose} maxWidth="sm" fullWidth>
          <DialogTitle>Start New Takeoff</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 14 }}>
              Create a lightweight legacy customer and jobsite, then start measuring immediately.
            </Typography>
            <div className={classes.quickStartFields}>
              <Autocomplete
                className={classes.quickStartFull}
                freeSolo
                options={props.clients || []}
                value={quickStartClient}
                inputValue={quickStartClientInput}
                onChange={(_, value) => setQuickStartClient(value)}
                onInputChange={(_, value, reason) => {
                  setQuickStartClientInput(value);
                  if (reason === 'input') setQuickStartClient(null);
                }}
                getOptionLabel={(option) => (typeof option === 'string' ? option : getClientName(option))}
                getOptionSelected={(option, value) => option?._id === value?._id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Client"
                    placeholder="Select existing or type new client name"
                    variant="outlined"
                    size="small"
                    required
                  />
                )}
              />
              <TextField
                className={classes.quickStartFull}
                label="Site Name"
                placeholder="Optional, defaults to address"
                variant="outlined"
                size="small"
                value={quickStartDraft.siteName}
                onChange={(e) => setQuickStartDraft(prev => ({ ...prev, siteName: e.target.value }))}
              />
              <TextField
                className={classes.quickStartFull}
                label="Address"
                variant="outlined"
                size="small"
                required
                value={quickStartDraft.address}
                onChange={(e) => setQuickStartDraft(prev => ({ ...prev, address: e.target.value }))}
              />
              <TextField
                className={classes.quickStartFull}
                label="Address 2"
                variant="outlined"
                size="small"
                value={quickStartDraft.address2}
                onChange={(e) => setQuickStartDraft(prev => ({ ...prev, address2: e.target.value }))}
              />
              <TextField
                label="City"
                variant="outlined"
                size="small"
                value={quickStartDraft.city}
                onChange={(e) => setQuickStartDraft(prev => ({ ...prev, city: e.target.value }))}
              />
              <TextField
                label="State"
                variant="outlined"
                size="small"
                value={quickStartDraft.state}
                onChange={(e) => setQuickStartDraft(prev => ({ ...prev, state: e.target.value }))}
              />
              <TextField
                label="Zip"
                variant="outlined"
                size="small"
                value={quickStartDraft.zip}
                onChange={(e) => setQuickStartDraft(prev => ({ ...prev, zip: e.target.value }))}
              />
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleQuickStartClose} disabled={quickStartSaving}>Cancel</Button>
            <Button
              color="primary"
              variant="contained"
              onClick={handleQuickStartSubmit}
              disabled={
                quickStartSaving ||
                !quickStartDraft.address.trim() ||
                !(quickStartClient?._id || String(quickStartClientInput || '').trim())
              }
              startIcon={quickStartSaving ? <CircularProgress size={16} color="inherit" /> : null}
            >
              Start Measuring
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={leaveDialogOpen}
          onClose={() => {
            pendingNavigationRef.current = null;
            setLeaveDialogOpen(false);
          }}
        >
          <DialogTitle>Unsaved takeoff changes</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              Save this takeoff before leaving, or continue and discard the current unsaved changes.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                pendingNavigationRef.current = null;
                setLeaveDialogOpen(false);
              }}
            >
              Stay
            </Button>
            <Button
              color="primary"
              variant="contained"
              disabled={saving}
              onClick={async () => {
                const saved = await saveTakeoffWithOverlays(takeoff?.name);
                if (saved) confirmPendingNavigation();
              }}
            >
              Save and leave
            </Button>
            <Button color="secondary" onClick={confirmPendingNavigation}>
              Leave without saving
            </Button>
          </DialogActions>
        </Dialog>
        <SiteTakeoffTour
          open={tourOpen}
          step={currentTourStep}
          stepIndex={tourStepIndex}
          totalSteps={SITE_TAKEOFF_V2_TOUR_STEPS.length}
          onNext={handleTourNext}
          onBack={handleTourBack}
          onClose={() => closeTour(true)}
          onTargetMissing={handleTourTargetMissing}
        />
      </div>
    </SiteTakeoffProvider>
  );
};

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user,
  clients: state.clients,
});

export default connect(mapStateToProps)(SiteTakeoffV3);
